import { Building2, Check } from "lucide-react";

export type SelectableCardItem = {
  id: string;
  title: string;
  /** Secondary line (e.g. an address). Wraps onto multiple lines. */
  subtitle?: string | null;
};

/**
 * A vertical list of selectable property/location cards — a check indicator, a
 * building icon, a title and an optional wrapping subtitle. Shared by the support
 * "My property" picker and the access-invite property picker so they stay identical.
 * The subtitle wraps (no truncation) so long addresses never overflow the card.
 */
export function SelectableCardList({
  items,
  selectedId,
  onSelect,
}: {
  items: SelectableCardItem[];
  selectedId: string;
  onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => {
        const on = selectedId === item.id;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            className={
              "flex w-full min-w-0 items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors " +
              (on
                ? "border-primary-600 bg-primary-50"
                : "border-hairline-strong bg-elevated")
            }
          >
            <span
              className={
                "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-pill border " +
                (on
                  ? "border-primary-600 bg-primary-600 text-white"
                  : "border-hairline-strong")
              }
            >
              {on ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
            </span>
            <Building2
              className="mt-0.5 h-4.5 w-4.5 shrink-0 text-ink-muted"
              strokeWidth={1.5}
            />
            <div className="min-w-0 flex-1">
              <p className="break-words text-body text-ink">{item.title}</p>
              {item.subtitle ? (
                <p className="break-words text-caption text-ink-muted">
                  {item.subtitle}
                </p>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}
